import {
  CheckCircle2,
  CircleAlert,
  CircleHelp,
  Database,
  MailCheck,
  Server,
  type LucideIcon,
} from "lucide-react";

export type PlatformHealthStatus =
  | "HEALTHY"
  | "WARNING"
  | "UNKNOWN";

export interface PlatformHealthItem {
  key: string;
  label: string;
  value: string;
  status: PlatformHealthStatus;
  icon: "API" | "DATABASE" | "EMAIL";
}

interface PilotHealthCardProps {
  items?: PlatformHealthItem[];
  availability?: number | null;
}

interface HealthStyle {
  icon: LucideIcon;
  containerClassName: string;
  statusIcon: LucideIcon;
  statusIconClassName: string;
}

const healthStyles: Record<
  PlatformHealthStatus,
  HealthStyle
> = {
  HEALTHY: {
    icon: CheckCircle2,
    containerClassName:
      "bg-emerald-100 text-emerald-700",
    statusIcon: CheckCircle2,
    statusIconClassName: "text-emerald-500",
  },
  WARNING: {
    icon: CircleAlert,
    containerClassName:
      "bg-amber-100 text-amber-700",
    statusIcon: CircleAlert,
    statusIconClassName: "text-amber-500",
  },
  UNKNOWN: {
    icon: CircleHelp,
    containerClassName:
      "bg-slate-200 text-slate-600",
    statusIcon: CircleHelp,
    statusIconClassName: "text-slate-400",
  },
};

const healthIcons: Record<
  PlatformHealthItem["icon"],
  LucideIcon
> = {
  API: Server,
  DATABASE: Database,
  EMAIL: MailCheck,
};

export function PilotHealthCard({
  items = [],
  availability = null,
}: PilotHealthCardProps) {
  const overallStatus =
    getOverallHealthStatus(items);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-950">
            Pilot health
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Operational readiness across the platform.
          </p>
        </div>

        <HealthHeaderIcon status={overallStatus} />
      </div>

      {items.length > 0 ? (
        <div className="mt-6 space-y-3">
          {items.map((item) => (
            <HealthItemRow
              key={item.key}
              item={item}
            />
          ))}
        </div>
      ) : (
        <PlatformHealthEmptyState />
      )}

      <AvailabilityPanel
        availability={availability}
      />
    </section>
  );
}

interface HealthHeaderIconProps {
  status: PlatformHealthStatus;
}

function HealthHeaderIcon({
  status,
}: HealthHeaderIconProps) {
  const style = healthStyles[status];
  const Icon = style.statusIcon;

  const containerClassName =
    status === "HEALTHY"
      ? "bg-emerald-50 text-emerald-600"
      : status === "WARNING"
        ? "bg-amber-50 text-amber-600"
        : "bg-slate-100 text-slate-500";

  return (
    <div
      className={[
        "flex h-10 w-10 items-center justify-center rounded-xl",
        containerClassName,
      ].join(" ")}
    >
      <Icon className="h-5 w-5" />
    </div>
  );
}

interface HealthItemRowProps {
  item: PlatformHealthItem;
}

function HealthItemRow({
  item,
}: HealthItemRowProps) {
  const style = healthStyles[item.status];
  const Icon = healthIcons[item.icon];
  const StatusIcon = style.statusIcon;

  return (
    <div className="flex items-center justify-between gap-4 rounded-xl bg-slate-50 p-4">
      <div className="flex min-w-0 items-center gap-3">
        <div
          className={[
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
            style.containerClassName,
          ].join(" ")}
        >
          <Icon className="h-4 w-4" />
        </div>

        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">
            {item.label}
          </p>

          <p className="mt-0.5 truncate text-xs text-slate-500">
            {item.value}
          </p>
        </div>
      </div>

      <StatusIcon
        className={[
          "h-5 w-5 shrink-0",
          style.statusIconClassName,
        ].join(" ")}
      />
    </div>
  );
}

function PlatformHealthEmptyState() {
  return (
    <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center">
      <CircleHelp className="mx-auto h-6 w-6 text-slate-400" />

      <p className="mt-3 text-sm font-semibold text-slate-900">
        Health monitoring not connected
      </p>

      <p className="mt-1 text-sm text-slate-500">
        API, database, and email delivery health will
        appear here after platform monitoring is added.
      </p>
    </div>
  );
}

interface AvailabilityPanelProps {
  availability: number | null;
}

function AvailabilityPanel({
  availability,
}: AvailabilityPanelProps) {
  if (availability === null) {
    return (
      <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-sm font-semibold text-slate-900">
          Platform availability
        </p>

        <p className="mt-2 text-3xl font-bold tracking-tight text-slate-500">
          —
        </p>

        <p className="mt-1 text-xs text-slate-500">
          Availability monitoring has not been configured.
        </p>
      </div>
    );
  }

  const safeAvailability = Math.min(
    Math.max(availability, 0),
    100
  );

  const isHealthy = safeAvailability >= 99;

  return (
    <div
      className={[
        "mt-6 rounded-xl border p-4",
        isHealthy
          ? "border-emerald-200 bg-emerald-50"
          : "border-amber-200 bg-amber-50",
      ].join(" ")}
    >
      <p
        className={[
          "text-sm font-semibold",
          isHealthy
            ? "text-emerald-900"
            : "text-amber-900",
        ].join(" ")}
      >
        Platform availability
      </p>

      <p
        className={[
          "mt-2 text-3xl font-bold tracking-tight",
          isHealthy
            ? "text-emerald-950"
            : "text-amber-950",
        ].join(" ")}
      >
        {formatAvailability(safeAvailability)}
      </p>

      <p
        className={[
          "mt-1 text-xs",
          isHealthy
            ? "text-emerald-700"
            : "text-amber-700",
        ].join(" ")}
      >
        Based on the current monitoring period
      </p>
    </div>
  );
}

function getOverallHealthStatus(
  items: PlatformHealthItem[]
): PlatformHealthStatus {
  if (items.length === 0) {
    return "UNKNOWN";
  }

  if (
    items.some(
      (item) => item.status === "WARNING"
    )
  ) {
    return "WARNING";
  }

  if (
    items.every(
      (item) => item.status === "HEALTHY"
    )
  ) {
    return "HEALTHY";
  }

  return "UNKNOWN";
}

function formatAvailability(
  availability: number
): string {
  const formatted = Number.isInteger(
    availability
  )
    ? availability.toFixed(0)
    : availability.toFixed(1);

  return `${formatted}%`;
}